import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { registerModalBackHandler } from '../../../lib/native';
import { CategoryBadge } from './CategoryBadge';
import { IMAGE_GRADIENTS, gradientFor } from './imageGradients';
import { viewportHeight } from '../../../lib/viewport';
import { PhotoCarousel, type PhotoCarouselPhoto } from './PhotoCarousel';

/**
 * PlaceSheet — the place detail bottom sheet opened from StopCard /
 * HighlightCard. Fixed backdrop + a max-520px sheet rising from the bottom:
 * a 4:3 hero (photo, or a crossfading gradient slideshow with pager dots),
 * close button, then title + category + location, context
 * chips, the host tip card, and a full-width inverted primary action with a
 * quiet secondary underneath.
 *
 * anim: 'slide' (sheet up, default) | 'fade' | 'rise'.
 */
export interface PlaceSheetChip {
  /** Tabler icon name (without `ti-` prefix). */
  icon: string;
  label: string;
  /** 'accent' renders the gold "book ahead" treatment. */
  tone?: 'default' | 'accent';
}

export interface PlaceSheetProps {
  title: string;
  category?: string;
  /** Location line, e.g. "Alfama, Lisbon". */
  location?: string;
  /** Photo hero — replaces the gradient slideshow when present. */
  photoUrl?: string | null;
  /** Multiple photos → swipeable carousel hero. Takes precedence over photoUrl. */
  photos?: PhotoCarouselPhoto[];
  photoFocal?: { x: number; y: number } | null;
  /** Palette key for the lead gradient + hero icon. */
  gradientKey?: string;
  /** Custom CSS background for the lead slide (overrides gradientKey). */
  gradient?: string;
  /** Tabler icon name for the hero glyph (overrides gradientKey icon). */
  icon?: string;
  /** Context chips row (best time, book ahead, price…). */
  chips?: PlaceSheetChip[];
  /** Host first name for the tip label ("Matheus's tip"). */
  hostName?: string;
  /** Host tip quote (serif). */
  tip?: string;
  /** Primary action. Hidden when omitted and no primarySlot. */
  primaryLabel?: string;
  primaryIcon?: string;
  onPrimary?: () => void;
  /** Replaces the primary button with a custom node (e.g. map-provider menu). */
  primarySlot?: ReactNode;
  /** Quiet secondary action under the primary. */
  secondaryLabel?: string;
  secondaryIcon?: string;
  onSecondary?: () => void;
  onClose: () => void;
  /** Entry animation. Default 'slide'. */
  anim?: 'slide' | 'fade' | 'rise';
  style?: CSSProperties;
  children?: ReactNode;
}

const ANIMS: Record<string, string> = {
  slide: 'sk-sheet-up .34s cubic-bezier(.25,.46,.45,.94) both',
  fade: 'sk-sheet-fade .28s cubic-bezier(.25,.46,.45,.94) both',
  rise: 'sk-sheet-rise .42s cubic-bezier(.16,.8,.3,1) both',
};

const KEYFRAMES = `
@keyframes sk-sheet-up{from{transform:translateY(100%)}to{transform:translateY(0)}}
@keyframes sk-sheet-fade{from{opacity:0;transform:scale(.975)}to{opacity:1;transform:scale(1)}}
@keyframes sk-sheet-rise{from{opacity:0;transform:translateY(52px)}to{opacity:1;transform:translateY(0)}}
@keyframes sk-backdrop-in{from{opacity:0}to{opacity:1}}
`;

export function PlaceSheet({
  title, category, location,
  photoUrl, photos, photoFocal, gradientKey, gradient, icon,
  chips = [], hostName, tip,
  primaryLabel, primaryIcon = 'navigation', onPrimary, primarySlot,
  secondaryLabel, secondaryIcon = 'message-circle', onSecondary,
  onClose, anim = 'slide', style, children,
}: PlaceSheetProps) {
  const g = gradientFor(gradientKey ?? 'travel');
  const leadBg = gradient || g.base;
  const glyph = icon || g.icon;

  // Gradient slideshow — lead slide first, then the rest of the palette.
  const allBases = Object.values(IMAGE_GRADIENTS).map((v) => v.base);
  const slides = [leadBg, ...allBases.filter((b) => b !== leadBg)];
  const [slide, setSlide] = useState(0);
  const carouselPhotos = photos?.length ? photos : photoUrl ? [{ url: photoUrl, focal: photoFocal }] : [];
  const showSlideshow = carouselPhotos.length === 0;

  useEffect(() => {
    if (!showSlideshow) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 2400);
    return () => clearInterval(t);
  }, [showSlideshow, slides.length]);

  // Android hardware back button closes this sheet instead of navigating
  // the route underneath it.
  useEffect(() => registerModalBackHandler(onClose), [onClose]);

  const [closeHover, setCloseHover] = useState(false);
  const [secondaryHover, setSecondaryHover] = useState(false);
  const [primaryHover, setPrimaryHover] = useState(false);

  return (
    <>
      <style>{KEYFRAMES}</style>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.72)', zIndex: 60,
          backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
          animation: 'sk-backdrop-in .22s ease both',
        }}
      />
      {/* Sheet */}
      <div
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, margin: '0 auto',
          width: '100%', maxWidth: 520, zIndex: 61,
          background: 'var(--surface)', borderRadius: '24px 24px 0 0',
          maxHeight: viewportHeight(0.92), overflowY: 'auto', display: 'flex', flexDirection: 'column',
          fontFamily: 'var(--font-sans)',
          animation: ANIMS[anim],
          ...style,
        }}
      >
        {/* Hero */}
        <div style={{ position: 'relative', aspectRatio: '4 / 3', flex: 'none', overflow: 'hidden', borderRadius: '24px 24px 0 0' }}>
          {carouselPhotos.length > 0 ? (
            <PhotoCarousel photos={carouselPhotos} alt={title} />
          ) : (
            <>
              {slides.map((bg, i) => (
                <div
                  key={bg}
                  style={{ position: 'absolute', inset: 0, background: bg, opacity: i === slide ? 1 : 0, transition: 'opacity .8s ease' }}
                />
              ))}
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(110% 95% at 28% 18%, rgba(255,255,255,.13), transparent 60%)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(104deg, rgba(255,255,255,.04) 0 2px, transparent 2px 30px)', pointerEvents: 'none' }} />
              <i className={`ti ti-${glyph}`} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 'calc(64px * var(--text-scale))', color: 'rgb(var(--cream) / .18)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5, pointerEvents: 'none' }}>
                {slides.map((bg, i) => (
                  <div
                    key={bg}
                    style={{
                      width: i === slide ? 16 : 5, height: 5, borderRadius: 999,
                      background: i === slide ? 'rgb(var(--cream) / .9)' : 'rgb(var(--cream) / .35)',
                      transition: 'all .4s ease',
                    }}
                  />
                ))}
              </div>
            </>
          )}
          <button className="sk-hit"
            type="button"
            onClick={onClose}
            aria-label="Close"
            onMouseEnter={() => setCloseHover(true)}
            onMouseLeave={() => setCloseHover(false)}
            style={{
              position: 'absolute', top: 14, right: 14, width: 36, height: 36, borderRadius: '50%',
              background: closeHover ? 'rgba(19,18,17,.82)' : 'rgba(19,18,17,.55)',
              backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgb(var(--cream))', transition: 'background .15s',
            }}
          >
            <i className="ti ti-x" style={{ fontSize: 'calc(14px * var(--text-scale))' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '22px 22px 40px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
              <span style={{ fontFamily: 'var(--font-serif)', fontSize: 'calc(26px * var(--text-scale))', fontWeight: 400, color: 'var(--text)', lineHeight: 1.1, flex: 1, minWidth: 0, textWrap: 'pretty' }}>{title}</span>
              {category ? (
                <CategoryBadge label={category} style={{ background: 'var(--bg)', padding: '3px 9px', marginTop: 5, flexShrink: 0 }} />
              ) : null}
            </div>
            {location ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-dim)', fontSize: 'var(--fs-caption)' }}>
                <i className="ti ti-map-pin" style={{ fontSize: 'calc(11px * var(--text-scale))' }} />{location}
              </span>
            ) : null}
          </div>

          {chips.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 24 }}>
              {chips.map((chip) => {
                const accent = chip.tone === 'accent';
                return (
                  <span
                    key={chip.label}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: accent ? 'rgb(var(--gold) / .08)' : 'var(--bg)',
                      border: accent ? '1px solid rgb(var(--gold) / .25)' : '1px solid var(--line)',
                      borderRadius: 999, padding: '6px 13px', fontSize: 'var(--fs-caption)',
                      color: accent ? 'rgb(var(--gold))' : 'var(--text-soft)',
                    }}
                  >
                    <i className={`ti ti-${chip.icon}`} style={{ fontSize: 'calc(12px * var(--text-scale))', color: accent ? 'rgb(var(--gold))' : 'var(--text-dim)' }} />
                    {chip.label}
                  </span>
                );
              })}
            </div>
          ) : null}

          {tip ? (
            <div style={{ background: 'var(--accent-tint)', border: '1px solid var(--accent-border)', borderRadius: 12, padding: '16px 18px', marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--avatar-bg)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-caption2)', color: 'var(--avatar-text)' }}>{(hostName || '?').charAt(0).toUpperCase()}</span>
                </div>
                <span style={{ color: 'rgb(var(--gold))', fontSize: 'var(--fs-caption)', fontWeight: 500 }}>{hostName ? `${hostName}'s tip` : 'Host tip'}</span>
              </div>
              <p style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-subhead)', fontWeight: 400, lineHeight: 1.6, color: 'var(--text)', textWrap: 'pretty' }}>{tip}</p>
            </div>
          ) : null}

          {children}

          {primarySlot ?? (primaryLabel ? (
            <button
              type="button"
              onClick={onPrimary}
              onMouseEnter={() => setPrimaryHover(true)}
              onMouseLeave={() => setPrimaryHover(false)}
              style={{
                width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', fontWeight: 600,
                background: 'var(--text)', color: 'var(--bg)', border: 'none', borderRadius: 999,
                padding: '15px 20px', cursor: 'pointer', transition: 'opacity .15s',
                opacity: primaryHover ? 0.88 : 1, marginBottom: 14,
              }}
            >
              <i className={`ti ti-${primaryIcon}`} style={{ fontSize: 'calc(14px * var(--text-scale))' }} />{primaryLabel}
            </button>
          ) : null)}

          {secondaryLabel ? (
            <button className="sk-hit"
              type="button"
              onClick={onSecondary}
              onMouseEnter={() => setSecondaryHover(true)}
              onMouseLeave={() => setSecondaryHover(false)}
              style={{
                width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', fontWeight: 500,
                background: 'transparent', color: secondaryHover ? 'var(--text)' : 'var(--text-dim)',
                border: 'none', cursor: 'pointer', padding: 6, transition: 'color .15s',
              }}
            >
              <i className={`ti ti-${secondaryIcon}`} style={{ fontSize: 'calc(13px * var(--text-scale))' }} />{secondaryLabel}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default PlaceSheet;
